import React, { Component } from 'react';
import {
  Button, Box, Tooltip,
} from '@mui/material';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Link, NavigateFunction } from 'react-router-dom';
import BillingDashboard from '../billing/BillingDashboard';

import { Trial, Enrollment } from '../../redux/types';
import ApiClient from '../../loopback/apiClient';
import ContentBox from '../layout/ContentBox';

type State = {
  paymentInProcess: boolean;
  confirmNoOrderNumber?: string;
  paymentToken?: string;
  error?: React.ReactNode;
  showBillingType: boolean;
  selectBilling: boolean;
};

class Payment extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      paymentInProcess: false,
      showBillingType: true,
      selectBilling: false,
    };
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { navigate } = this.props;
    const { error, paymentToken } = this.state;
    if (JSON.stringify(prevState) === JSON.stringify(this.state)) {
      return;
    }

    if (paymentToken) {
      window.setTimeout(() => {
        navigate(`/paymentSuccess?paymentToken=${paymentToken}`);
      }, 3000);
    }

    if (error) {
      this.resetPaymentButton();
    }
  }

  getWarningText() {
    const { confirmNoOrderNumber } = this.state;
    if (confirmNoOrderNumber) {
      return (
        <FormattedMessage id="enrollmentsmanager.orderNumberWarningText" />
      );
    }

    return '';
  }

  getTooltipTextNotApprove = (): string => {
    const { intl } = this.props;
    return intl.formatMessage({ id: 'payment.enrollmentPending' });
  };

  getProceedPaymentText() {
    const { confirmNoOrderNumber } = this.state;
    if (confirmNoOrderNumber) {
      return <FormattedMessage id="confirm" />;
    }
    return <FormattedMessage id="payment.proceedToPayment" />;
  }

  chooseBillingAddress = () => {
    const { trial } = this.props;
    if (trial.isDRT) {
      this.setState({ showBillingType: false, selectBilling: true });
    } else {
      this.createOrUpdateInvoiceAndProcessPayment();
    }
  };

  onBillingAddressSelected = (id: number): void => {
    this.createOrUpdateInvoiceAndProcessPayment(id);
  };

  proceedPayment = (invoiceId: number) => {
    const { trial } = this.props;
    ApiClient.authorizePayment(trial.id, invoiceId, 'rec')
      .then((paymentToken) => {
        if (paymentToken.error) {
          this.setState({ error: paymentToken.error.errorMessage });
        } else {
          this.setState({ paymentToken });
        }
      })
      .catch((err) => {
        this.setState({ error: JSON.stringify(err) });
      });
  };

  createOrUpdateInvoiceAndProcessPayment = (billingId?: number) => {
    this.setState({
      paymentInProcess: true,
      error: undefined,
    });
    const { enrollment } = this.props;
    ApiClient.createOrUpdateInvoice(enrollment.id, 'rec', billingId)
      .then((invoice) => {
        this.proceedPayment(invoice.id);
      })
      .catch(() => {
        this.setState({
          error: <FormattedMessage id="payment.payoneUrlGenerationError" />,
        });
        this.resetPaymentButton();
      });
  };

  resetPaymentButton = () => {
    this.setState({
      paymentInProcess: false,
    });
  };

  renderSelectPayment() {
    const {
      paymentInProcess,
      error,
      paymentToken,
    } = this.state;

    const { onCancel, enrollment } = this.props;

    const enrollmentStatusPending = enrollment.status !== 'approved';
    return (
      <ContentBox title={<FormattedMessage id="payment.heading" />}>
        <p>
          <FormattedMessage id="payment.description" />
        </p>
        <Box display="flex" sx={{ mt: 2 }} gap={1} justifyContent="flex-end">
          <Tooltip
            title={
              enrollmentStatusPending ? this.getTooltipTextNotApprove() : ''
            }
          >
            <Button
              variant="contained"
              id="InvoiceDialog-createInvoiceButton"
              onClick={this.chooseBillingAddress}
              color="primary"
              disabled={enrollmentStatusPending || paymentInProcess}
            >
              {this.getProceedPaymentText()}
            </Button>
          </Tooltip>
          <Button variant="contained" color="secondary" onClick={onCancel}>
            <FormattedMessage id="genericCancel" />
          </Button>
        </Box>
        {error && <div className="mt3 red">{error}</div>}
        {paymentToken && (
          <div className="mt2">
            <FormattedMessage id="payment.redirectMessageInvoice" />
            <Link to={`/paymentSuccess?paymentToken=${paymentToken}`}>
              <FormattedMessage id="payment.redirectLink" />
            </Link>
          </div>
        )}
      </ContentBox>
    );
  }

  render() {
    const { onCancel } = this.props;
    const { showBillingType } = this.state;

    if (showBillingType) {
      return this.renderSelectPayment();
    }
    return (
      <ContentBox title={<FormattedMessage id="billingAddress.heading" />}>
        <Box style={{ width: 1200 }}>
          <BillingDashboard onSelect={this.onBillingAddressSelected} />
        </Box>
        <Box className="mt3">
          <Button
            variant="contained"
            id="InvoiceDialog-createInvoiceButton"
            onClick={() => this.setState({ showBillingType: true, selectBilling: false })}
            color="primary"
          >
            <FormattedMessage id="back" />
          </Button>
          <Button
            variant="contained"
            className="ml3"
            color="secondary"
            onClick={onCancel}
          >
            <FormattedMessage id="genericCancel" />
          </Button>
        </Box>
      </ContentBox>
    );
  }
}

type Props = {
  onCancel: () => void;
  trial: Trial;
  enrollment: Enrollment;
  intl: {
    formatMessage: (arg0: { [key: string]: string }) => string;
  };
  navigate: NavigateFunction;
};
export default injectIntl(Payment);
